import { useMemo } from "react";
import { useSelector } from "react-redux";
import * as moment from "moment";
import useCheckoutForm from "./useCheckoutForm";
// Icons
import LocalDiningOutlinedIcon from "@material-ui/icons/LocalDiningOutlined";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import StoreOutlinedIcon from "@material-ui/icons/StoreOutlined";
import CreditCardOutlinedIcon from "@material-ui/icons/CreditCardOutlined";
import LocalAtmOutlinedIcon from "@material-ui/icons/LocalAtmOutlined";
import FlashOnOutlinedIcon from "@material-ui/icons/FlashOnOutlined";
import AccessTimeOutlinedIcon from "@material-ui/icons/AccessTimeOutlined";
// Constants
import {
  ORDER_TYPES,
  PAYMENT_METHODS,
  DELIVERY_PICKUP_TYPES,
} from "../utils/Constants";
import {
  convertToMoment,
  getTimeIntervals,
  getTodayTimeIntervals,
  deriveAbsoluteSlotTimes,
  findOvernightExtensionSlot,
  isRunningHoursMidnightPattern,
} from "../utils/TimeUtils";
import { modifySlotsByType } from "../utils/utils";
import {
  getActiveClosures,
  isTimeInClosure,
  filterSlotsByClosures,
} from "../utils/ClosureUtils";

function useDynamicHotelData() {
  const { hotel, tableId } = useSelector((state) => state.hotel);
  const { cart } = useSelector((state) => state);
  const { formValues } = useCheckoutForm();

  const closures = useMemo(
    () => getActiveClosures(hotel, formValues.orderType),
    [hotel, formValues.orderType]
  );

  const modifySlotsWithType = (res) => {
    return (
      modifySlotsByType(
        hotel,
        cart,
        formValues,
        res,
        formValues?.deliveryTime
      ) || []
    );
  };

  // Holiday-aware slot filter for a given date. The "overnight extension"
  // morning slot in a midnight-store pattern (e.g. 12am–3am) belongs to the
  // PREVIOUS day's shift, not the day it's stored under. So:
  //   - If the chosen date is a holiday/closed but its previous day is
  //     operational, keep ONLY the overnight extension slot. The user can
  //     still order during the early-morning tail of last night's shift,
  //     even though the rest of today is closed.
  //   - If the chosen date is normal but its previous day is closed/holiday,
  //     EXCLUDE the overnight extension slot. There was no shift last night
  //     to continue, so the morning slot doesn't operate.
  // Pure non-midnight days are left untouched.
  const filterRunningHoursForDate = (
    running,
    dateMoment,
    orderTypeValue
  ) => {
    if (!Array.isArray(running) || running.length === 0) return [];

    const selectedDateStr = dateMoment.format("YYYY-MM-DD");
    const selectedDayInfo = hotel?.workingInfo?.find(
      (elem) => elem.day === dateMoment.day()
    );
    const selectedIsHoliday = (hotel?.shop_hoilidays || []).some(
      (h) => h?.date === selectedDateStr
    );
    const selectedClosed =
      !selectedDayInfo ||
      selectedDayInfo.is_closed === 1 ||
      selectedIsHoliday;

    const prevDate = dateMoment.clone().subtract(1, "day");
    const prevDayInfo = hotel?.workingInfo?.find(
      (elem) => elem.day === prevDate.day()
    );
    const prevIsHoliday = (hotel?.shop_hoilidays || []).some(
      (h) => h?.date === prevDate.format("YYYY-MM-DD")
    );
    const prevHours =
      prevDayInfo?.hotelDayInfo?.find(
        (elem) => elem.type === orderTypeValue
      )?.running_hours || [];
    const prevOperational =
      !!prevDayInfo &&
      prevDayInfo.is_closed !== 1 &&
      !prevIsHoliday &&
      prevHours.length > 0;

    const overnightSlot = findOvernightExtensionSlot(running);
    const isOvernightSlot = (rh) =>
      overnightSlot &&
      rh?.start === overnightSlot.start &&
      rh?.stop === overnightSlot.stop;

    if (selectedClosed) {
      if (overnightSlot && prevOperational) {
        return running.filter(isOvernightSlot);
      }
      return [];
    }

    if (overnightSlot && !prevOperational) {
      return running.filter((rh) => !isOvernightSlot(rh));
    }
    return running;
  };

  const hotelStatus = useMemo(() => {
    if (hotel?.hotelStatus?.[0]) {
      return hotel.hotelStatus[0];
    }
    return {
      active: false,
      active_reason: "Hotel network is down",
    };
  }, [hotel]);

  const hotelOrderStatus = useMemo(() => {
    if (hotel) {
      return hotel.online_order_active === 1;
    }
    return {
      active: false,
    };
  }, [hotel]);

  // Narrow override for online_order_active only. When the current moment
  // is inside an active closure window AND the user has selected a slot
  // outside every closure, the backend's online_order_active=0 is
  // almost certainly due to the closure (not a manual offline / network
  // issue / etc.), so the cart / add-to-cart action for that future slot
  // is safe to enable. Anything else (openingAvailable, pickup/delivery
  // availability, hotelStatus.active) is left untouched — those reflect
  // separate state we can't safely override from the closure signal.
  // Gated on is_preorder=1 so we don't bypass hotels that have
  // intentionally disabled pre-ordering.
  const isOrderableForSelectedSlot = useMemo(() => {
    if (!hotel) return false;
    if (hotel.is_preorder !== 1) return false;
    if (!formValues?.deliveryTime) return false;
    const slotEpoch = moment(formValues.deliveryTime).valueOf();
    if (!slotEpoch || isNaN(slotEpoch)) return false;
    if (slotEpoch <= moment().valueOf()) return false;
    if (isTimeInClosure(slotEpoch, closures)) return false;
    // Only treat online_order_active=0 as a closure side-effect when a
    // closure really is active right now. Without this, an offline-for-
    // other-reasons hotel would still appear orderable.
    if (!isTimeInClosure(moment().valueOf(), closures)) return false;
    return true;
  }, [hotel, formValues?.deliveryTime, closures]);

  const getOrderTypes = useMemo(() => {
    if (!hotel) {
      return [];
    }
    const orderTypes = [];

    if (hotel.pickup) {
      orderTypes.push({
        type: ORDER_TYPES.PICKUP,
        title: "Pickup",
        active: hotel.pickupAvailable,
        enabled: hotel.pickup,
        Icon: StoreOutlinedIcon,
      });
    }
    if (hotel.delivery) {
      orderTypes.push({
        type: ORDER_TYPES.DELIVERY,
        title: "Delivery",
        active: hotel.deliveryAvailable,
        enabled: hotel.delivery,
        Icon: LocalShippingOutlinedIcon,
      });
    }
    if (hotel.dine_in) {
      orderTypes.push({
        type: ORDER_TYPES.DINEIN,
        title: "Dine in",
        active: hotel.openingAvailable,
        Icon: LocalDiningOutlinedIcon,
      });
    }
    return orderTypes;
  }, [hotel]);

  const getPaymentMethods = useMemo(() => {
    if (!hotel) {
      return [];
    }
    const paymentTypes = [];
    // console.log("logggg", hotel?.payment_gateway)

    if (hotel.online_payment_enable) {
      hotel?.payment_gateway?.map((item) => {
        paymentTypes.push({
          type: PAYMENT_METHODS.ONLINE_PAYMENT,
          title: item?.gateway,
          label: item?.label,
          Icon: CreditCardOutlinedIcon,
          active: 1,
          id: item?.gateway_id,
        });
      });
    }
    if (tableId) {
      const tablePayments = hotel?.payment_table_side;
      tablePayments?.forEach((p) => {
        paymentTypes.push({
          ...p,
          title: p.type
            ? `${p.type[0].toUpperCase()}${p.type.substring(1)}`
            : "",
          Icon: LocalAtmOutlinedIcon,
        });
      });
    } else {
      const methods =
        formValues.orderType === ORDER_TYPES.PICKUP
          ? hotel.payment_pickup
          : hotel.payment_delivery;
      if (methods) {
        methods
          .filter((elem) => elem.active === 1)
          .forEach((p) => {
            paymentTypes.push({
              ...p,
              // title: p.type,
              title: p.type
                ? `${p.type[0].toUpperCase()}${p.type.substring(1)}`
                : "",
              Icon: LocalAtmOutlinedIcon,
            });
          });
      }
    }
    return paymentTypes;
  }, [hotel, formValues.orderType]);

  const getDeliveryPickupTypes = useMemo(() => {
    if (!hotel) {
      return [];
    }
    const data = [
      {
        type: DELIVERY_PICKUP_TYPES.ASAP,
        title: "ASAP",
        Icon: FlashOnOutlinedIcon,
      },
      {
        type: DELIVERY_PICKUP_TYPES.SCHEDULED,
        title: "Scheduled",
        Icon: AccessTimeOutlinedIcon,
      },
    ];

    return data;
  }, [hotel]);

  const isMultipleOrderType = useMemo(() => {
    if (getOrderTypes.filter((s) => s.enabled === 1).length > 1) {
      // if (getOrderTypes.filter((s) => console.log("ssssssssss", s))) {
      return true;
    }
    return false;
  }, [getOrderTypes]);

  const getAvailableDays = useMemo(() => {
    if (!hotel) {
      return [];
    }
    return hotel.workingInfo.map((elem) => elem.day);
  }, [hotel]);

  const isAsapAvailable = useMemo(() => {
    if (!hotel) {
      return false;
    }
    const bufTime =
      formValues.orderType === "DELIVERY"
        ? hotel.delivery_buffer_time
        : hotel.pickup_buffer_time;
    const time = moment().add(bufTime, "minutes");

    // Check if ASAP time falls within a scheduled closure window
    if (isTimeInClosure(time.valueOf(), closures)) {
      return false;
    }

    const dayInfo = hotel.workingInfo.find(
      (elem) => elem.day === moment().day()
    );
    if (dayInfo && dayInfo.hotelDayInfo) {
      const hours = dayInfo.hotelDayInfo.find(
        (elem) => elem.type === formValues.orderType
      );
      // Apply the holiday-aware filter so the overnight-extension morning
      // slot (e.g. 12am–3am) is dropped when yesterday was closed/holiday.
      // Without this, ASAP would falsely report "open" at 3am today after
      // a holiday yesterday — the morning slot is the previous day's
      // overnight tail and that overnight didn't operate.
      const filteredRunning = filterRunningHoursForDate(
        hours?.running_hours,
        moment(),
        formValues.orderType
      );
      const data = filteredRunning.map((elem) => {
        const start = convertToMoment(elem.start);
        const end = convertToMoment(elem.stop);
        return time.isBetween(start, end);
      });
      return data && data.length > 0
        ? data.reduce((prev, curr) => prev || curr)
        : false;
    } else {
      return false;
    }
  }, [hotel, formValues.orderType, closures]);

  const getTodayDeliveryIntervalSlots = useMemo(() => {
    // console.log("hotel 1111111111", hotel);
    if (!hotel) {
      return [];
    }
    const bufTime =
      formValues.orderType === ORDER_TYPES.DELIVERY
        ? hotel.delivery_buffer_time
        : formValues.orderType === ORDER_TYPES.PICKUP
        ? hotel.pickup_buffer_time
        : 30;
    const dayInfo = hotel.workingInfo.find(
      (elem) => elem.day === moment().day()
    );
    // console.log("form values 111111111", formValues.orderType);
    // console.log("cartttttttttt", cart);
    if (dayInfo && dayInfo.hotelDayInfo) {
      let orderTypeValue = formValues.orderType;
      if (formValues.orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
        orderTypeValue = ORDER_TYPES.OPENING;
      }
      // console.log("dayInfo.hotelDayInfo 1111111", dayInfo.hotelDayInfo);
      // console.log("orderTypeValue 111111", orderTypeValue);
      const hours = dayInfo.hotelDayInfo.find(
        (elem) => elem.type === orderTypeValue
      );
      // console.log("hours 111111", hours?.running_hours);

      const filteredRunning = filterRunningHoursForDate(
        hours?.running_hours,
        moment(),
        orderTypeValue
      );
      const data = filteredRunning.map((elem) => {
        const options = getTodayTimeIntervals(
          elem.start,
          elem.stop,
          15,
          bufTime
        );
        return options;
      });
      // console.log("data 1111111", data);

      const res =
        data && data.length > 0
          ? data.reduce((prev, curr) => [...prev, ...curr])
          : [];
      const result = modifySlotsWithType(res);
      return filterSlotsByClosures(result, formValues.deliveryTime || moment(), closures);
    } else {
      return [];
    }
  }, [hotel, formValues.orderType, formValues.deliveryTime, closures]);

  const getDeliveryIntervalSlots = useMemo(() => {
    // console.log("hotel 22222222222", hotel);

    if (!hotel) {
      return [];
    }
    const bufTime =
      formValues.orderType === ORDER_TYPES.DELIVERY
        ? hotel.delivery_buffer_time
        : formValues.orderType === ORDER_TYPES.PICKUP
        ? hotel.pickup_buffer_time
        : 30;
    const dayInfo = hotel.workingInfo.find(
      (elem) => elem.day === moment(formValues.deliveryTime).day()
    );
    // console.log("form values 2222222222", formValues.orderType);

    if (dayInfo && dayInfo.hotelDayInfo) {
      let orderTypeValue = formValues.orderType;
      if (formValues.orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
        orderTypeValue = ORDER_TYPES.OPENING;
      }
      // console.log("dayInfo.hotelDayInfo 2222222", dayInfo.hotelDayInfo);
      // console.log("orderTypeValue 2222222", orderTypeValue);
      const hours = dayInfo.hotelDayInfo.find(
        (elem) => elem.type === orderTypeValue
      );
      // console.log("hours 222222222", hours?.running_hours);
      const filteredRunning = filterRunningHoursForDate(
        hours?.running_hours,
        moment(formValues.deliveryTime),
        orderTypeValue
      );
      // When the selected date is today, generate intervals via the today-
      // aware helper so already-past times are dropped. For genuinely future
      // dates every clock time is in the future, so the regular generator
      // works as before.
      const isSelectedToday = moment(formValues.deliveryTime).isSame(
        moment(),
        "date"
      );
      const data = filteredRunning.map((elem) => {
        const options = isSelectedToday
          ? getTodayTimeIntervals(elem.start, elem.stop, 15, bufTime)
          : getTimeIntervals(elem.start, elem.stop, 15, bufTime);
        return options;
      });
      // Source running_hours order isn't chronological — an "all day"
      // schedule like [{09:00-23:59}, {00:00-02:00}] lists the evening
      // first, but for a future date the morning extension (12 AM-2 AM)
      // is the chronologically earliest part of the day and should lead
      // the dropdown so the auto-select picks the actual first slot.
      const nonEmptyData = data.filter((arr) => arr && arr.length > 0);
      const sortedData = nonEmptyData.sort((a, b) => {
        const timeA = moment(a[0], "hh:mm a");
        const timeB = moment(b[0], "hh:mm a");
        if (timeA.isBefore(timeB)) return -1;
        if (timeA.isAfter(timeB)) return 1;
        return 0;
      });
      const res =
        sortedData.length > 0
          ? sortedData.reduce((prev, curr) => [...prev, ...curr])
          : [];
      const result = modifySlotsWithType(res);
      return filterSlotsByClosures(result, formValues.deliveryTime, closures);
    } else {
      return [];
    }
  }, [hotel, formValues.orderType, formValues.deliveryTime, closures]);

  // Label used by the "<TYPE> available until <X>" hint, plus a flag for the
  // late-night badge. Independent of slot generation. Works regardless of
  // running_hours array order. Algorithm:
  //   1. Resolve each slot to absolute local moments (handles within-slot
  //      and cross-slot midnight crossings).
  //   2. Sort by start, find the slot that contains "now" (or the latest
  //      slot if the shop is currently closed).
  //   3. Walk forward through any contiguous follow-on slots — that chain's
  //      final end is the closing time.
  //   4. isMidnightStore = chain's final end is past today's local midnight.
  const todayClosingInfo = useMemo(() => {
    const empty = { availableUntilLabel: undefined, isMidnightStore: false };
    if (!hotel) return empty;
    let orderTypeValue = formValues.orderType;
    if (formValues.orderType === ORDER_TYPES.TABLE_BOOKING_AND_ORDERING) {
      orderTypeValue = ORDER_TYPES.OPENING;
    }
    const dayInfo = hotel.workingInfo.find(
      (elem) => elem.day === moment().day()
    );
    if (!dayInfo || !dayInfo.hotelDayInfo) return empty;
    const hours = dayInfo.hotelDayInfo.find(
      (elem) => elem.type === orderTypeValue
    );
    const rawRunning = hours?.running_hours;
    if (!rawRunning || rawRunning.length === 0) return empty;

    // Today must operate to have a "right now" available-until or badge.
    // If today is a holiday or marked closed, suppress both — even if the
    // shop's intrinsic pattern is a midnight store.
    const todayDateStr = moment().format("YYYY-MM-DD");
    const todayIsHoliday = (hotel.shop_hoilidays || []).some(
      (h) => h?.date === todayDateStr
    );
    if (todayIsHoliday || dayInfo.is_closed === 1) return empty;

    // Apply the holiday-aware filter so today's morning slot (which is
    // structurally the PREVIOUS day's overnight extension) is dropped when
    // yesterday was closed/holiday. The chain walk + tomorrow peek then
    // operate on today's actual operational hours.
    const running = filterRunningHoursForDate(
      rawRunning,
      moment(),
      orderTypeValue
    );
    if (!running || running.length === 0) return empty;

    // Tomorrow's STRUCTURAL pattern — used only to verify whether the
    // overnight slot is genuinely a recurring schedule entry, not to gate
    // the extension on tomorrow's holiday status. The overnight tail of
    // today's shift still operates when today is open even if tomorrow is
    // a one-off holiday.
    const tomorrowMoment = moment().add(1, "day");
    const tomorrowDayIdx = tomorrowMoment.day();
    const tomorrowDayInfo = hotel.workingInfo.find(
      (elem) => elem.day === tomorrowDayIdx
    );
    const tomorrowSlotKeys = new Set(
      (
        tomorrowDayInfo?.hotelDayInfo?.find(
          (elem) => elem.type === orderTypeValue
        )?.running_hours || []
      ).map((rh) => `${rh.start}|${rh.stop}`)
    );

    const slots = deriveAbsoluteSlotTimes(running);
    if (slots.length === 0) return empty;

    // A slot is treated as "non-operational" only when a single closure
    // covers it end-to-end. A closure that just trims the front of the
    // slot (e.g. 12:00–12:05 against a 12:00–02:00 overnight slot) still
    // leaves operational time after it, so the slot — and the midnight
    // pattern — remain valid.
    const isSlotFullyClosed = (slotStartMs, slotEndMs) => {
      if (!Array.isArray(closures) || closures.length === 0) return false;
      return closures.some(
        (c) =>
          typeof c?.schedule_start_time === "number" &&
          typeof c?.schedule_end_time === "number" &&
          c.schedule_start_time <= slotStartMs &&
          c.schedule_end_time >= slotEndMs
      );
    };

    // Revert cross-slot shifts when EITHER tomorrow's pattern doesn't list
    // the matching slot, OR the shifted slot is fully consumed by an
    // upcoming scheduled closure. Both cases mean today's overnight wrap
    // doesn't actually operate and the badge should drop. A *partial*
    // closure (one that ends before the slot's end) leaves operational
    // time and must not trigger the revert.
    slots.forEach((s) => {
      if (!s.crossSlotShifted) return;
      const key = `${s.raw?.start}|${s.raw?.stop}`;
      const noTomorrowMatch = !tomorrowSlotKeys.has(key);
      const fullyClosed = isSlotFullyClosed(
        s.localStart.valueOf(),
        s.localEnd.valueOf()
      );
      if (noTomorrowMatch || fullyClosed) {
        s.localStart = s.localStart.clone().subtract(1, "day");
        s.localEnd = s.localEnd.clone().subtract(1, "day");
        s.crossSlotShifted = false;
      }
    });

    // The "Late night orders available" badge is a property of the
    // *schedule shape*, not of today's particular closures. Drive it
    // straight off the raw running_hours so a partial closure (e.g. a
    // 22:00–00:10 window inside a 18:00–03:00 store) — which still
    // leaves operational time after midnight — keeps the badge visible.
    // Fall back to the post-revert slot flags in case the raw pattern
    // check ever returns false but the derived slots disagree.
    const intrinsicMidnight =
      isRunningHoursMidnightPattern(rawRunning) ||
      slots.some((s) => s.crossSlotShifted || s.withinSlotWrap);

    const now = moment();

    // Yesterday's overnight extension anchors to today's early-morning
    // hours. At e.g. 2:09 AM on a midnight store, "now" lives inside
    // yesterday's morning extension (operational from today 00:00 to
    // today 03:00) — today's own running_hours has its morning slot
    // forward-shifted to tomorrow, so without this the chain logic would
    // miss the current operational slot entirely.
    const yesterdayMoment = moment().subtract(1, "day");
    const yesterdayDayInfo = hotel.workingInfo.find(
      (elem) => elem.day === yesterdayMoment.day()
    );
    const yesterdayIsHoliday = (hotel.shop_hoilidays || []).some(
      (h) => h?.date === yesterdayMoment.format("YYYY-MM-DD")
    );
    const yesterdayOperational =
      !!yesterdayDayInfo &&
      yesterdayDayInfo.is_closed !== 1 &&
      !yesterdayIsHoliday;
    const yesterdayRunning =
      yesterdayDayInfo?.hotelDayInfo?.find(
        (elem) => elem.type === orderTypeValue
      )?.running_hours || [];
    if (yesterdayOperational && yesterdayRunning.length > 0) {
      const yesterdayDerived = deriveAbsoluteSlotTimes(yesterdayRunning);
      yesterdayDerived.forEach((s) => {
        // deriveAbsoluteSlotTimes anchors to TODAY (convertToMoment uses
        // today as dayStart). Subtract a day so yesterday's evening maps
        // to actual yesterday and yesterday's morning extension (cross-
        // shifted +1 day during derive) lands on today's early morning.
        const ls = s.localStart.clone().subtract(1, "day");
        const le = s.localEnd.clone().subtract(1, "day");
        if (le.isAfter(now)) {
          slots.push({
            ...s,
            localStart: ls,
            localEnd: le,
          });
        }
      });
    }

    slots.sort((a, b) => a.localStart.valueOf() - b.localStart.valueOf());
    const TOLERANCE_MIN = 5;
    let chainEnd = null;
    let lastEnd = null;
    const currentSlot = slots.find((s) =>
      now.isBetween(s.localStart, s.localEnd, null, "[]")
    );
    if (currentSlot) {
      chainEnd = currentSlot.localEnd.clone();
      lastEnd = currentSlot.localEnd;
      for (const s of slots) {
        if (s === currentSlot) continue;
        const gapMin = Math.abs(s.localStart.diff(lastEnd, "minutes"));
        if (
          gapMin <= TOLERANCE_MIN &&
          s.localStart.isSameOrAfter(lastEnd) &&
          !isSlotFullyClosed(s.localStart.valueOf(), s.localEnd.valueOf())
        ) {
          chainEnd = s.localEnd.clone();
          lastEnd = s.localEnd;
        }
      }
    } else {
      chainEnd = slots[slots.length - 1].localEnd.clone();
      lastEnd = chainEnd;
    }

    // Tomorrow peek — extend the chain into tomorrow's morning slots that
    // are clock-contiguous with today's tail. Skipped when the candidate
    // slot's start is inside an active closure (so a fully-closed early-
    // morning slot can't artificially push "available until" past midnight).
    const tomorrowRunning =
      tomorrowDayInfo?.hotelDayInfo?.find(
        (elem) => elem.type === orderTypeValue
      )?.running_hours || [];
    const tomorrowAbsoluteSlots = tomorrowRunning
      .map((rh) => {
        const ls = convertToMoment(rh.start);
        let le = convertToMoment(rh.stop);
        if (!ls.isValid() || !le.isValid()) return null;
        if (le.isSameOrBefore(ls)) le = le.clone().add(1, "day");
        return {
          raw: rh,
          localStart: ls.clone().add(1, "day"),
          localEnd: le.clone().add(1, "day"),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.localStart.valueOf() - b.localStart.valueOf());
    for (const ts of tomorrowAbsoluteSlots) {
      const gapMin = Math.abs(ts.localStart.diff(lastEnd, "minutes"));
      if (
        gapMin <= TOLERANCE_MIN &&
        ts.localStart.isSameOrAfter(lastEnd) &&
        !isSlotFullyClosed(ts.localStart.valueOf(), ts.localEnd.valueOf())
      ) {
        chainEnd = ts.localEnd.clone();
        lastEnd = ts.localEnd;
      }
    }

    // Final closure clip — only pull chainEnd back when a closure starts
    // before chainEnd AND extends past it (i.e., the closure cuts off
    // operations without reopening before close). If the closure ends
    // before chainEnd, ordering simply resumes after the gap and the
    // natural chainEnd still applies — the affected slots are already
    // filtered out by filterSlotsByClosures.
    let earliestBlockingClosureStart = null;
    (closures || []).forEach((c) => {
      const cStart = c?.schedule_start_time;
      const cEnd = c?.schedule_end_time;
      if (typeof cStart !== "number" || typeof cEnd !== "number") return;
      if (
        cStart > now.valueOf() &&
        cStart < chainEnd.valueOf() &&
        cEnd >= chainEnd.valueOf()
      ) {
        if (
          earliestBlockingClosureStart === null ||
          cStart < earliestBlockingClosureStart
        ) {
          earliestBlockingClosureStart = cStart;
        }
      }
    });
    if (earliestBlockingClosureStart !== null) {
      chainEnd = moment(earliestBlockingClosureStart);
    }

    // Recompute midnight after the clip — if the closure pulled chainEnd
    // back before midnight, the badge must follow.
    const chainCrossesMidnight = chainEnd.isAfter(moment().endOf("D"));

    return {
      availableUntilLabel: chainEnd.format("hh:mm a"),
      isMidnightStore: intrinsicMidnight || chainCrossesMidnight,
    };
  }, [hotel, formValues.orderType, closures]);

  const availableUntilLabel = todayClosingInfo.availableUntilLabel;
  const isMidnightStore = todayClosingInfo.isMidnightStore;

  return {
    hotel,
    hotelStatus,
    hotelOrderStatus,
    getOrderTypes,
    getPaymentMethods,
    getDeliveryPickupTypes,
    isMultipleOrderType,
    getAvailableDays,
    isAsapAvailable,
    getTodayDeliveryIntervalSlots,
    getDeliveryIntervalSlots,
    modifySlotsWithType,
    availableUntilLabel,
    isMidnightStore,
    isOrderableForSelectedSlot,
  };
}

export default useDynamicHotelData;
